/** Request/Response interceptors - centralized interceptors. */

import axios, { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { v4 as uuidv4 } from "uuid";

import { HEADER_REQUEST_ID, HEADER_TRACE_ID, CONTENT_TYPE_JSON } from "@/lib/constants/api";
import type { ApiResponse, ErrorResponse } from "@/lib/api/types";
import { logHubApiError } from "@/lib/utils/hub-api-error-log";
import {
  gatewayHttpFailureFromBody,
  hubDetailFieldsFromErrorBody,
  messageFromFastApiErrorBody,
} from "@/lib/utils/response";
import {
  ensureAccessTokenFresh,
  invalidateSession,
  isSessionInvalidationInProgress,
  refreshSessionTokens,
} from "@/lib/auth/permissions";
import { getGatewayBaseUrl } from "@/config/gateway";
import { authFetchCredentials } from "@/config/authStorage";

function logInterceptedHttpError(error: AxiosError, payload: ErrorResponse): void {
  const method = String(error.config?.method || "").toUpperCase();
  const path = String(error.config?.url || "");
  logHubApiError("api.interceptor", payload, { method, path });
}

function log401Unauthorized(phase: string, cfg?: InternalAxiosRequestConfig) {
  const path = cfg?.url ? `${cfg.baseURL ?? ""}${cfg.url}` : "";
  console.error(`[auth][401] ${phase}`, path || "(no path)");
}

export function isNonSessionUnauthorizedDetail(detail: string): boolean {
  const normalized = detail.trim().toLowerCase();
  if (!normalized) return false;
  if (normalized.includes("not authenticated") || normalized.includes("session expired")) {
    return false;
  }
  return (
    normalized.includes("gateway identity is required") ||
    normalized.includes("gateway trust proof is required") ||
    normalized.includes("tenant identity is required") ||
    normalized.includes("actor identity is required") ||
    normalized.includes("mesh caller is not trusted") ||
    normalized.includes("mesh trust proof is invalid") ||
    normalized.includes("authentication required for analytics ai surfaces") ||
    normalized.includes("ai analytics upstream rejected gateway identity envelope") ||
    normalized.includes("authorization permission denied") ||
    normalized.includes("authorization_permission_denied") ||
    normalized.includes("registry.route.unknown") ||
    normalized.includes("registry_route_unknown") ||
    normalized.includes("missing authorization permission") ||
    normalized.includes("not authorized") ||
    normalized.includes("permission denied")
  );
}

export function unauthorizedDetailFromBody(body: unknown): string {
  if (!body || typeof body !== "object") return "";
  const data = body as {
    detail?: unknown;
    error?: unknown;
    error_code?: unknown;
    details?: unknown;
  };
  const parts: string[] = [];
  if (typeof data.error === "string" && data.error.trim()) {
    parts.push(data.error.trim());
  } else if (data.error && typeof data.error === "object") {
    const message = (data.error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      parts.push(message.trim());
    }
    const code = (data.error as { code?: unknown }).code;
    if (typeof code === "string" && code.trim()) {
      parts.push(code.trim());
    }
  }
  if (typeof data.error_code === "string" && data.error_code.trim()) {
    parts.push(data.error_code.trim());
  }
  if (data.details && typeof data.details === "object" && !Array.isArray(data.details)) {
    const required = (data.details as { required_permission?: unknown }).required_permission;
    if (typeof required === "string" && required.trim()) {
      parts.push(required.trim());
    }
  }
  const fastApi = messageFromFastApiErrorBody(data);
  if (fastApi) {
    parts.push(fastApi);
  }
  return parts.join(" ");
}

function unauthorizedDetailFromAxiosError(error: AxiosError): string {
  return unauthorizedDetailFromBody(error.response?.data);
}

export async function cookieSessionStillAlive(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const res = await fetch(`${getGatewayBaseUrl()}/v1/auth/refresh`, {
      method: "POST",
      credentials: authFetchCredentials(),
      headers: { "Content-Type": "application/json" },
    });
    return res.ok;
  } catch {
    return false;
  }
}

export const SESSION_EXPIRED_ERROR_CODE = "SESSION_EXPIRED";

function createSessionExpiredError(): ErrorResponse {
  return {
    success: false,
    error: {
      code: SESSION_EXPIRED_ERROR_CODE,
      message: "Session expired",
      details: { status: 401 },
    },
    meta: {
      request_id: "session-expired",
      timestamp: new Date().toISOString(),
      version: "v1",
    },
  };
}

function isSessionExpiredError(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "success" in error &&
    (error as ErrorResponse).success === false &&
    (error as ErrorResponse).error?.code === SESSION_EXPIRED_ERROR_CODE
  );
}

/** 401: refresh (API + Keycloak JS), retry once; invalidate session on failure. */
export async function handleUnauthorizedResponse(
  error: unknown,
  axiosInstance: AxiosInstance
): Promise<unknown> {
  if (!axios.isAxiosError(error) || error.response?.status !== 401 || !error.config) {
    return Promise.reject(errorInterceptor(error));
  }
  const cfg = error.config as InternalAxiosRequestConfig & { _authRetry?: boolean };
  const unauthorizedDetail = unauthorizedDetailFromAxiosError(error);
  if (isNonSessionUnauthorizedDetail(unauthorizedDetail)) {
    log401Unauthorized("non-session upstream identity 401; keeping session", cfg);
    return Promise.reject(errorInterceptor(error));
  }
  if (cfg._authRetry) {
    log401Unauthorized("retry still 401; refresh already attempted", cfg);
    invalidateSession();
    return Promise.reject(createSessionExpiredError());
  }
  cfg._authRetry = true;
  const refreshed = await refreshSessionTokens();
  if (refreshed) {
    return axiosInstance.request(cfg);
  }
  if (await cookieSessionStillAlive()) {
    log401Unauthorized("refresh failed but cookie session alive; keeping session", cfg);
    return Promise.reject(errorInterceptor(error));
  }
  log401Unauthorized("refresh failed; invalidating session", cfg);
  invalidateSession();
  return Promise.reject(createSessionExpiredError());
}

/**
 * Request interceptor - add request ID, project ID, and headers.
 *
 * @param config - Axios request config
 * @returns Modified config
 */
export async function requestInterceptor(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  const sessionOk = await ensureAccessTokenFresh();
  if (!sessionOk) {
    return Promise.reject(createSessionExpiredError());
  }

  const existingRequestId = String(config.headers[HEADER_REQUEST_ID] || "").trim();
  const requestId = existingRequestId || uuidv4().replace(/-/g, "");
  const existingTraceId = String(config.headers[HEADER_TRACE_ID] || "").trim();
  const traceId = existingTraceId || requestId;

  config.headers[HEADER_REQUEST_ID] = requestId;
  config.headers[HEADER_TRACE_ID] = traceId;
  config.headers["Accept"] = CONTENT_TYPE_JSON;
  config.withCredentials = true;

  // Handle FormData (file uploads) - delete Content-Type to let browser set it with boundary
  // For non-FormData requests, set Content-Type to application/json
  const isFormData = config.data instanceof FormData;
  if (isFormData) {
    // Delete Content-Type header to let browser set it with boundary
    delete config.headers["Content-Type"];
  } else {
    // Set Content-Type for non-FormData requests
    config.headers["Content-Type"] = CONTENT_TYPE_JSON;
  }

  return config;
}

/**
 * Response interceptor - handle standard response format.
 *
 * @param response - Axios response
 * @returns Modified response with data
 */
export function responseInterceptor<T>(response: AxiosResponse<ApiResponse<T>>): AxiosResponse<ApiResponse<T>> {
  return response;
}

/**
 * Error interceptor - handle errors and convert to standard format.
 *
 * @param error - Axios error
 * @returns Error response
 */
export function errorInterceptor(error: unknown): ErrorResponse {
  if (isSessionExpiredError(error)) {
    return error as ErrorResponse;
  }

  if (axios.isAxiosError(error)) {
    // Handle Axios errors
    if (error.response) {
      // Server responded with error
      const data = error.response.data as ErrorResponse | { detail?: string };

      // 403 account locked or suspended access
      if (error.response.status === 403 && typeof window !== "undefined") {
        const d = error.response.data as { detail?: unknown; error?: { message?: string } } | undefined;
        const msg =
          (d && typeof d === "object" && d.error && typeof d.error.message === "string" ? d.error.message : messageFromFastApiErrorBody(d)) || "";
        if (msg.toLowerCase().includes("locked")) {
          window.location.replace("/account-locked");
        } else {
          window.dispatchEvent(
            new CustomEvent("intake:forbidden", {
              detail: {
                message: msg || "Forbidden",
                status: 403,
                path: String(error.config?.url || ""),
                requestId: String(error.config?.headers?.[HEADER_REQUEST_ID] || "").trim() || undefined,
              },
            })
          );
        }
      }

      if (error.response.status === 401 && isSessionInvalidationInProgress()) {
        return createSessionExpiredError();
      }

      if (data && typeof data === "object" && "success" in data && data.success === false) {
        const hub = hubDetailFieldsFromErrorBody(data);
        const existing = (data as ErrorResponse).error;
        const mergedDetails = { ...(existing.details ?? {}), ...hub };
        const payload =
          Object.keys(hub).length === 0
            ? (data as ErrorResponse)
            : {
                ...(data as ErrorResponse),
                error: {
                  ...existing,
                  details: mergedDetails,
                },
              };
        logInterceptedHttpError(error, payload);
        return payload;
      }

      const failure = gatewayHttpFailureFromBody(error.response.status, error.response.statusText, data);
      const responseRequestId = String(error.response.headers?.["x-request-id"] || "").trim();
      const configRequestId = String(error.config?.headers?.[HEADER_REQUEST_ID] || "").trim();
      const payload: ErrorResponse = {
        success: false,
        error: failure,
        meta: {
          request_id: responseRequestId || configRequestId || "unknown",
          timestamp: new Date().toISOString(),
          version: "v1",
        },
      };
      logInterceptedHttpError(error, payload);
      return payload;
    }

    if (error.request) {
      // Request made but no response
      const timeoutLike = String(error.code || "").toUpperCase() === "ECONNABORTED";
      const payload: ErrorResponse = {
        success: false,
        error: {
          code: timeoutLike ? "TIMEOUT_ERROR" : "NETWORK_ERROR",
          message: timeoutLike ? "Request timed out. Try fewer files or retry." : "Network error. Please check your connection.",
          details: {
            message: error.message,
            code: String(error.code || ""),
          },
        },
        meta: {
          request_id: (error.config?.headers?.[HEADER_REQUEST_ID] as string) || "unknown",
          timestamp: new Date().toISOString(),
          version: "v1",
        },
      };
      logHubApiError("api.interceptor", payload, {
        method: String(error.config?.method || "").toUpperCase(),
        path: String(error.config?.url || ""),
      });
      return payload;
    }
  }

  // Unknown error
  const payload: ErrorResponse = {
    success: false,
    error: {
      code: "UNKNOWN_ERROR",
      message: "An unexpected error occurred",
      details: {
        error: String(error),
      },
    },
    meta: {
      request_id: "unknown",
      timestamp: new Date().toISOString(),
      version: "v1",
    },
  };
  logHubApiError("api.interceptor", payload);
  return payload;
}
